import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Alert, LinearProgress, Stack,
  Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon, Circle as DotIcon, HelpOutline as HelpIcon } from '@mui/icons-material';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';

// Operator-only — single-shot snapshot of fleet hardware + queue + log
// health. Backend gathers per-node stats over SSH in parallel and runs a
// DB consistency sweep + error-log exception bucketing. 60 s in-memory
// cache; the Refresh button bypasses with ?refresh=1.

function pct(used, total) {
  if (!total) return null;
  return Math.round((used / total) * 100);
}
function fmtBytes(gb) {
  if (gb == null) return '—';
  return gb >= 1024 ? `${(gb / 1024).toFixed(1)} TB` : `${gb} GB`;
}
function fmtUptime(s) {
  if (!s) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}

function severityChip(sev) {
  const map = {
    high:   { color: 'error',   label: 'HIGH' },
    medium: { color: 'warning', label: 'MED' },
    low:    { color: 'default', label: 'LOW' },
  };
  const cfg = map[sev] || map.low;
  return <Chip size="small" color={cfg.color} label={cfg.label} sx={{ minWidth: 56 }} />;
}

// Role-aware load colour. For encoder roles, saturation is the desired
// state during a job — pegging all cores means ffmpeg/dcpomatic is doing
// what it's supposed to. We only flag when the run-queue exceeds cores
// (true overload, processes waiting). Non-encoder roles (head node,
// utility, delivery) keep the stricter thresholds because saturation
// there usually signals contention rather than productive work.
function loadColor(load1, cores, role) {
  if (load1 == null || !cores) return 'default';
  const ratio = load1 / cores;
  const isEncoder = role === 'watermark_encoder' || role === 'dcp_encoder';
  if (isEncoder) {
    if (ratio >= 2.0) return 'error';     // 2× cores = serious queueing
    if (ratio >= 1.25) return 'warning';  // some queueing
    return 'success';                     // saturated or below = healthy
  }
  if (ratio >= 0.9) return 'error';
  if (ratio >= 0.6) return 'warning';
  return 'success';
}

function ramColor(used, total) {
  const p = pct(used, total);
  if (p == null) return 'default';
  if (p >= 90) return 'error';
  if (p >= 75) return 'warning';
  return 'success';
}

function diskColor(free, total) {
  if (free == null || !total) return 'default';
  const freePct = (free / total) * 100;
  if (freePct < 5) return 'error';
  if (freePct < 15) return 'warning';
  return 'success';
}

function FleetHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/api/operator/fleet-health${forceRefresh ? '?refresh=1' : ''}`);
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load fleet health.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  return (
    <Box>
      <PageHeader
        title="Fleet Health"
        subtitle="Per-node hardware stats, queue depth, schema drift, and recent backend exceptions."
        actions={
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={() => load(true)}
            disabled={loading}
          >
            {loading ? 'Probing…' : 'Refresh'}
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && !data && <LinearProgress sx={{ mb: 2 }} />}

      {data && (
        <Stack spacing={3}>
          {/* Snapshot meta */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Checked {new Date(data.checked_at).toLocaleString()}
              {data.from_cache && ` · cached ${data.cache_age_seconds}s ago`}
            </Typography>
          </Box>

          {/* Queue summary */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Queue</Typography>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <QueueStat label="Watermark pending"     value={data.queue?.watermark_pending} />
              <QueueStat label="Watermark processing"  value={data.queue?.watermark_processing} />
              <QueueStat label="HLS active"            value={data.queue?.hls_active} />
              <QueueStat label="DCP pending"           value={data.queue?.dcp_pending} />
              <QueueStat label="DCP in flight"         value={data.queue?.dcp_in_flight} />
              <QueueStat label="Packages unsynced"     value={data.queue?.packages_unsynced} />
            </Stack>
          </Paper>

          {/* Nodes table */}
          <Paper>
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="subtitle1">Nodes ({data.nodes?.length || 0})</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Node</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell align="right">
                      <HeaderHelp
                        label="Cores"
                        title="vCPU count reported by `nproc`. For VMs this is the count assigned to the VM, not the underlying physical CPU."
                      />
                    </TableCell>
                    <TableCell align="right">
                      <HeaderHelp
                        label="Load 1m"
                        title="Linux 1-minute load average expressed as a percentage of core capacity (load ÷ cores × 100). 100% = every core busy. Encoder roles are EXPECTED to hit 100% during a job — chip stays green up to 100%, warning at 125% (real queueing), error at 200%. Non-encoder roles use stricter thresholds (60% warn / 90% error). Hover the chip for raw load avg + 5m/15m values."
                      />
                    </TableCell>
                    <TableCell align="right">
                      <HeaderHelp
                        label="RAM"
                        title="Memory in use as a percentage of total. ≥75% warning, ≥90% error. Hover the chip for absolute MB."
                      />
                    </TableCell>
                    <TableCell align="right">
                      <HeaderHelp
                        label="Disk free"
                        title="Free space on / (root). <15% turns warning, <5% turns error. Hover the chip for total size."
                      />
                    </TableCell>
                    <TableCell align="right">
                      <HeaderHelp
                        label="Uptime"
                        title="Time since the node last booted (`uptime`). Watermark agent / DCP worker is restarted independently — see the Encoders / DCP nodes panels for service uptime."
                      />
                    </TableCell>
                    <TableCell align="right">
                      <HeaderHelp
                        label="Kernel warns (24h)"
                        title="Count of dmesg lines in the last 24 h matching: OOM kills, killed-process events, hardware errors, I/O errors, and CIFS 'sock stuck' / EFAULT events. NAS contention typically shows up here as 'sock stuck for 15 seconds' bursts. Counts persist until the lines roll out of the kernel ring buffer."
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.nodes || []).map((n) => (
                    <TableRow key={`${n.name}-${n.ip}`}>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <DotIcon sx={{ fontSize: 10, color: n.online ? 'success.main' : 'error.main' }} />
                          <Box>
                            <Typography variant="body2">{n.name}</Typography>
                            {n.error && (
                              <Tooltip title={n.error}>
                                <Typography variant="caption" color="error">{n.error.slice(0, 40)}</Typography>
                              </Tooltip>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{n.role}</TableCell>
                      <TableCell><code>{n.ip}</code></TableCell>
                      <TableCell align="right">{n.cores ?? '—'}</TableCell>
                      <TableCell align="right">
                        {n.load1 != null && n.cores ? (
                          <Tooltip
                            title={`load avg ${n.load1.toFixed(2)} on ${n.cores} cores · 5m ${n.load5?.toFixed(2) ?? '—'} · 15m ${n.load15?.toFixed(2) ?? '—'}`}
                          >
                            <Chip
                              size="small"
                              color={loadColor(n.load1, n.cores, n.role)}
                              label={`${Math.round((n.load1 / n.cores) * 100)}%`}
                            />
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {n.ram_total_mb ? (
                          <Tooltip title={`${n.ram_used_mb} / ${n.ram_total_mb} MB`}>
                            <Chip
                              size="small"
                              color={ramColor(n.ram_used_mb, n.ram_total_mb)}
                              label={`${pct(n.ram_used_mb, n.ram_total_mb)}%`}
                            />
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {n.disk_free_gb != null ? (
                          <Tooltip title={`${n.disk_free_gb} GB free / ${n.disk_total_gb} GB total`}>
                            <Chip
                              size="small"
                              color={diskColor(n.disk_free_gb, n.disk_total_gb)}
                              label={fmtBytes(n.disk_free_gb)}
                            />
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">{fmtUptime(n.uptime_seconds)}</TableCell>
                      <TableCell align="right">
                        {n.kernel_warnings > 0 ? (
                          <Chip size="small" color="warning" label={n.kernel_warnings} />
                        ) : (n.online ? '0' : '—')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Issues */}
          <Paper>
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="subtitle1">
                Issues ({(data.issues || []).filter((i) => i.severity !== 'low').length} actionable)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Stale leases, schema drift, stuck deliveries, recent backend exception buckets.
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 80 }}>Severity</TableCell>
                    <TableCell sx={{ width: 200 }}>Source</TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>Count</TableCell>
                    <TableCell>Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.issues || []).length === 0 ? (
                    <TableRow><TableCell colSpan={4}><em>No issues detected.</em></TableCell></TableRow>
                  ) : (
                    [...data.issues]
                      .sort((a, b) => {
                        const rank = { high: 0, medium: 1, low: 2 };
                        return (rank[a.severity] ?? 9) - (rank[b.severity] ?? 9);
                      })
                      .map((i, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{severityChip(i.severity)}</TableCell>
                          <TableCell><code>{i.source}</code></TableCell>
                          <TableCell align="right">{i.count > 0 ? i.count : '—'}</TableCell>
                          <TableCell>{i.message}</TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}
    </Box>
  );
}

// Column-header label with a subtle help icon. Hovering surfaces the
// metric definition + how the chip-color thresholds are derived.
function HeaderHelp({ label, title }) {
  return (
    <Tooltip title={title} arrow placement="top">
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
        {label}
        <HelpIcon sx={{ fontSize: 14, opacity: 0.5 }} />
      </Box>
    </Tooltip>
  );
}

function QueueStat({ label, value }) {
  if (value == null) return null;
  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6">{value}</Typography>
    </Box>
  );
}

export default FleetHealth;
